import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ShieldX, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";

const TYPE_META = {
  admin_probe: { label: "Admin API probe", cls: "bg-red-50 text-red-700 border-red-200" },
  login_failed: { label: "Staff login failed", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  portal_login_failed: { label: "Portal login failed", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const eventDetail = (e) => {
  if (e.type === "admin_probe") return `${e.method || ""} ${e.path || ""} without credentials`;
  if (e.type === "portal_login_failed") return `${e.email || "no email"} · ref ${e.reference || "—"}`;
  return `${e.email || "no email"}${e.reason ? ` · ${e.reason}` : ""}`;
};

export function SecurityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/security-events").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div data-testid="security-panel">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-black tracking-tight mb-1">Security</h2>
          <p className="text-sm text-[#6B7280]">Unauthorised staff-endpoint probes and failed sign-ins, kept for 30 days. Senior admin only.</p>
        </div>
        <button data-testid="security-refresh-btn" onClick={load}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider border border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#12263A] transition-colors">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div data-testid="security-stat-csrf" className="border border-[#E5E7EB] bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">
            <ShieldX size={14} className="text-red-600" /> Unauthorised admin requests (7 days)
          </div>
          <div className="text-4xl font-black">{data ? data.probes_7d : "—"}</div>
        </div>
        <div data-testid="security-stat-logins" className="border border-[#E5E7EB] bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">
            <KeyRound size={14} className="text-amber-600" /> Failed sign-ins (7 days)
          </div>
          <div className="text-4xl font-black">{data ? data.login_failed_7d : "—"}</div>
        </div>
      </div>

      {data && data.events.length === 0 ? (
        <div data-testid="security-events-empty" className="border border-dashed border-[#E5E7EB] p-10 text-center text-sm text-[#6B7280]">
          <ShieldCheck size={22} className="mx-auto mb-2 text-emerald-600" />
          No security events in the last 30 days.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table data-testid="security-events-table" className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Detail</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {(data?.events || []).map((e) => {
                const meta = TYPE_META[e.type] || { label: e.type, cls: "bg-gray-50 text-gray-700 border-gray-200" };
                return (
                  <tr key={e.id} data-testid="security-event-row" className="border-b border-[#F3F4F6] last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap text-[#6B7280]">{fmtTime(e.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-3 break-all">{eventDetail(e)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-[#6B7280]">{e.ip || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
